import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { simulateMetrics } from '../Email-Marketing/shared/analyticsSimulator';
import {
  fetchCampaigns,
  fetchCampaignById,
  createCampaign as createCampaignApi,
  updateCampaign as updateCampaignApi,
  patchCampaign,
  deleteCampaignApi,
  pauseCampaign,
  sendTestEmail as sendTestEmailApi,
  addCampaignAudience,
  updateCampaignAudience as updateCampaignAudienceApi,
  deleteCampaignAudience as deleteCampaignAudienceApi,
  bulkCampaigns,
} from '../Email-Marketing/shared/api.js';
import {
  sample,
  transformCampaignRecord,
  calculateEstimatedReach,
} from '../Email-Marketing/campaigns/campaignTransforms.js';

// Replaces the Email-Marketing `useCampaignsStore` zustand store. The pure
// record transforms live in `campaigns/campaignTransforms.js`.

// Campaigns are merged by id rather than replaced, so a paged load never drops
// a campaign that was fetched individually.
// Past tense per bulk action, for the toast. 'edit' does not take a 'd'.
const PAST_TENSE = { pause: 'paused', resume: 'resumed', delete: 'deleted', edit: 'updated' };

// The table renders capitalised statuses ('Paused', 'Sending'); the API speaks lowercase.
const capitalizeStatus = (status) =>
  typeof status === 'string' && status.length
    ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    : status;

const mergeById = (existing, incoming) => {
  const map = new Map();
  for (const c of existing) {
    const cid = String(c.id || c._id || '');
    if (cid) map.set(cid, c);
  }
  for (const c of incoming) {
    const cid = String(c.id || c._id || '');
    if (cid) map.set(cid, c);
  }
  return Array.from(map.values());
};

export const loadCampaigns = createAsyncThunk(
  'emailCampaigns/loadCampaigns',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await fetchCampaigns(params);

      // The API has returned three different envelope shapes over time.
      let campaigns = [];
      if (Array.isArray(response?.campaigns)) {
        campaigns = response.campaigns;
      } else if (Array.isArray(response)) {
        campaigns = response;
      } else if (Array.isArray(response?.data?.campaigns)) {
        campaigns = response.data.campaigns;
      }

      return {
        campaigns: campaigns.map(transformCampaignRecord),
        pagination: response?.pagination || null,
      };
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const loadCampaignById = createAsyncThunk(
  'emailCampaigns/loadCampaignById',
  async (id) => {
    if (!id) return null;
    const idStr = String(id);
    try {
      const data = await fetchCampaignById(idStr);
      if (!data) return null;
      return { id: idStr, campaign: transformCampaignRecord(data) };
    } catch (error) {
      console.error('Failed to load campaign by ID:', id, error);
      return null;
    }
  }
);

export const createCampaign = createAsyncThunk(
  'emailCampaigns/createCampaign',
  async (campaignData) => {
    const response = await createCampaignApi(campaignData);
    const newCampaign = response.campaign || response; // both envelope shapes
    return transformCampaignRecord(newCampaign);
  }
);

export const updateCampaign = createAsyncThunk(
  'emailCampaigns/updateCampaign',
  async ({ id, campaignData }) => {
    const idStr = String(id);
    // A payload missing name/subject is a partial edit, which PATCHes.
    const isPartial = !campaignData.name || !campaignData.subject;
    const apiCall = isPartial ? patchCampaign : updateCampaignApi;
    const response = await apiCall(idStr, campaignData);
    const updated =
      response.campaign || response.data?.campaign || response.data || response;
    return { id: idStr, campaign: transformCampaignRecord(updated) };
  }
);

/**
 * Audience entries are added, edited and removed one at a time -- the campaign's other
 * audiences must survive untouched. The server answers with the whole campaign, recipients
 * already rebuilt as the union of every audience.
 */
export const addAudience = createAsyncThunk(
  'emailCampaigns/addAudience',
  async ({ campaignId, audience }) => {
    const response = await addCampaignAudience(String(campaignId), audience);
    const updated = response.campaign || response.data?.campaign || response;
    return { id: String(campaignId), campaign: transformCampaignRecord(updated) };
  }
);

export const updateAudience = createAsyncThunk(
  'emailCampaigns/updateAudience',
  async ({ campaignId, audienceId, audience }) => {
    const response = await updateCampaignAudienceApi(String(campaignId), String(audienceId), audience);
    const updated = response.campaign || response.data?.campaign || response;
    return { id: String(campaignId), campaign: transformCampaignRecord(updated) };
  }
);

export const deleteAudience = createAsyncThunk(
  'emailCampaigns/deleteAudience',
  async ({ campaignId, audienceId }) => {
    const response = await deleteCampaignAudienceApi(String(campaignId), String(audienceId));
    const updated = response.campaign || response.data?.campaign || response;
    return { id: String(campaignId), campaign: transformCampaignRecord(updated) };
  }
);

export const sendTestEmail = createAsyncThunk(
  'emailCampaigns/sendTestEmail',
  async ({ campaignId, testEmails }) => {
    const emailArray =
      typeof testEmails === 'string'
        ? testEmails.split(',').map((email) => email.trim()).filter(Boolean)
        : testEmails;
    return await sendTestEmailApi(campaignId, emailArray);
  }
);

export const removeCampaign = createAsyncThunk(
  'emailCampaigns/removeCampaign',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await deleteCampaignApi(id);
      dispatch(addAlert({ message: 'Campaign deleted successfully', severity: 'success' }));
      return id;
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      dispatch(
        addAlert({ message: error.message || 'Failed to delete campaign', severity: 'error' })
      );
      return rejectWithValue(error.message);
    }
  }
);


/**
 * Runs pause, resume or delete over every selected campaign in one request.
 *
 * The server answers per campaign rather than all-or-nothing, so this lands the rows that
 * went through and leaves the ones that were refused selected -- the user can see what was
 * skipped and why instead of hunting for it in a list that silently shrank.
 */
export const bulkCampaignAction = createAsyncThunk(
  'emailCampaigns/bulkAction',
  async ({ action, ids, changes }, { dispatch, rejectWithValue }) => {
    const targetIds = (ids || []).map(String).filter(Boolean);
    if (targetIds.length === 0) return rejectWithValue('No campaigns selected');

    try {
      const { results, summary } = await bulkCampaigns(action, targetIds, changes);
      const succeededIds = results.filter((r) => r.success).map((r) => String(r.id));
      const failed = results.filter((r) => !r.success);

      const verb = PAST_TENSE[action] || `${action}d`;
      if (summary.succeeded > 0) {
        dispatch(
          addAlert({
            message: failed.length === 0
              ? `${summary.succeeded} campaign${summary.succeeded === 1 ? '' : 's'} ${verb}`
              : `${summary.succeeded} of ${summary.total} campaigns ${verb} — ${failed.length} skipped`,
            severity: failed.length === 0 ? 'success' : 'warning',
          })
        );
      } else {
        // Every row was refused: surface the first reason rather than a bare failure count.
        dispatch(
          addAlert({
            message: failed[0]?.message || `Could not ${action} the selected campaigns`,
            severity: 'error',
          })
        );
      }

      return { action, results, summary, succeededIds };
    } catch (error) {
      console.error(`Bulk ${action} failed:`, error);
      dispatch(
        addAlert({
          message: error.message || `Failed to ${action} the selected campaigns`,
          severity: 'error',
        })
      );
      return rejectWithValue(error.message);
    }
  }
);

// Optimistically flips status, then confirms or reverts against the backend.
export const toggleStatus = createAsyncThunk(
  'emailCampaigns/toggleStatus',
  async (id, { dispatch, getState }) => {
    const campaign = getState().emailCampaigns.campaigns.find((c) => c.id === id);
    if (!campaign) return null;

    const isCurrentlyPaused = campaign.status === 'Paused';
    const action = isCurrentlyPaused ? 'resume' : 'pause';
    const nextStatus = isCurrentlyPaused ? 'Sending' : 'Paused';

    dispatch(setCampaignStatus({ id, status: nextStatus }));

    try {
      const updatedCampaign = await pauseCampaign(id, action);
      dispatch(
        addAlert({
          message: `Campaign ${isCurrentlyPaused ? 'resumed' : 'paused'} successfully`,
          severity: 'success',
        })
      );
      return { id, campaign: transformCampaignRecord(updatedCampaign) };
    } catch (error) {
      console.error(`Failed to ${action} campaign:`, error);
      dispatch(
        setCampaignStatus({ id, status: isCurrentlyPaused ? 'Paused' : 'Sending' })
      );
      dispatch(
        addAlert({
          message: `Failed to ${action} campaign: ${error.message || 'Unknown error'}`,
          severity: 'error',
        })
      );
      return null;
    }
  }
);

// Alerts raised off the campaigns page are stashed in localStorage and replayed
// on arrival, so the two thunks below carry the timer side effects.
export const showAlert = createAsyncThunk(
  'emailCampaigns/showAlert',
  async ({ message, severity = 'success' }, { dispatch }) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 5);
    const newAlert = { id, message, severity, open: true };
    const isOnCampaignsPage = window.location.pathname === '/campaigns';

    if (isOnCampaignsPage) {
      dispatch(pushAlert(newAlert));
      setTimeout(() => dispatch(dismissAlert(id)), 5000);
    } else {
      dispatch(stashAlert(newAlert));
    }
    return newAlert;
  }
);

export const loadStoredAlerts = createAsyncThunk(
  'emailCampaigns/loadStoredAlerts',
  async (_, { dispatch, getState }) => {
    let stored = [];
    try {
      const saved = localStorage.getItem('campaign_stored_alerts');
      if (saved) stored = JSON.parse(saved);
    } catch (e) {
      console.error('[store] Failed to load stored alerts from localStorage:', e);
      stored = getState().emailCampaigns.storedAlerts;
    }

    if (stored && stored.length > 0) {
      localStorage.removeItem('campaign_stored_alerts');
      dispatch(clearStoredAlerts());

      // Stagger mounting of each alert for a premium look.
      stored.forEach((alert, index) => {
        setTimeout(() => {
          const id = Date.now().toString() + '-' + index;
          dispatch(pushAlert({ ...alert, id, open: true }));
          setTimeout(() => dispatch(dismissAlert(id)), 5000);
        }, index * 400);
      });
    }
    return stored;
  }
);

const initialState = {
  campaigns: [],
  loading: false,
  // Bulk-selection state. The campaigns table shows its checkbox column unconditionally, so
  // there is no mode to track -- only which rows are ticked. Ids are held as strings so a
  // Mongo _id and an id compare equal.
  selectedIds: [],
  bulkBusy: false,
  // The last batch that had something skipped, so the UI can say which rows and why. A count
  // in a toast is enough when everything worked; it is not when six of twenty were refused.
  lastBulkResult: null,
  pagination: { currentPage: 1, itemsPerPage: 20, totalItems: 0, totalPages: 0 },
  alerts: [],
  storedAlerts: [],
};

const emailCampaignsSlice = createSlice({
  name: 'emailCampaigns',
  initialState,
  reducers: {
    add: (state, action) => {
      const campaign = action.payload;
      state.campaigns = [
        transformCampaignRecord({
          ...campaign,
          id: campaign.id || campaign._id || Date.now().toString(),
          createdAt: campaign.createdAt || new Date().toISOString(),
        }),
        ...state.campaigns,
      ];
    },
    update: (state, action) => {
      const { id, patch } = action.payload;
      const idStr = String(id);
      state.campaigns = state.campaigns.map((campaign) =>
        String(campaign.id) === idStr || String(campaign._id) === idStr
          ? { ...campaign, ...patch, id: idStr, _id: idStr, updatedAt: new Date().toISOString() }
          : campaign
      );
    },
    duplicate: (state, action) => {
      const original = state.campaigns.find((c) => c.id === action.payload);
      if (!original) return;
      state.campaigns = [
        {
          ...original,
          id: Date.now().toString(),
          name: `${original.name} (Copy)`,
          status: 'Draft',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          sentAt: null,
          scheduledAt: null,
          metrics: { sent: 0, opens: 0, clicks: 0, conversions: 0 },
        },
        ...state.campaigns,
      ];
    },
    // Optimistic placeholders the composer adds before the API round-trip, then
    // drops once the real record arrives.
    prependCampaign: (state, action) => {
      state.campaigns = [action.payload, ...state.campaigns];
    },
    dropCampaign: (state, action) => {
      state.campaigns = state.campaigns.filter((c) => c.id !== action.payload);
    },
    setCampaignStatus: (state, action) => {
      const { id, status } = action.payload;
      state.campaigns = state.campaigns.map((c) =>
        c.id === id ? { ...c, status, updatedAt: new Date().toISOString() } : c
      );
    },
    pause: (state, action) => {
      state.campaigns = state.campaigns.map((c) =>
        c.id === action.payload
          ? { ...c, status: 'Paused', updatedAt: new Date().toISOString() }
          : c
      );
    },
    resume: (state, action) => {
      state.campaigns = state.campaigns.map((c) =>
        c.id === action.payload
          ? { ...c, status: 'Sent', updatedAt: new Date().toISOString() }
          : c
      );
    },
    declareWinner: (state, action) => {
      const { campaignId, winningVariant } = action.payload;
      state.campaigns = state.campaigns.map((campaign) =>
        campaign.id === campaignId && campaign.abTest?.enabled
          ? {
              ...campaign,
              abTest: {
                ...campaign.abTest,
                winner: winningVariant,
                completedAt: new Date().toISOString(),
              },
              updatedAt: new Date().toISOString(),
            }
          : campaign
      );
    },
    // --- Bulk selection ---
    toggleSelected: (state, action) => {
      const id = String(action.payload);
      state.selectedIds = state.selectedIds.includes(id)
        ? state.selectedIds.filter((s) => s !== id)
        : [...state.selectedIds, id];
    },
    selectMany: (state, action) => {
      const incoming = (action.payload || []).map(String);
      state.selectedIds = Array.from(new Set([...state.selectedIds, ...incoming]));
    },
    deselectMany: (state, action) => {
      const drop = new Set((action.payload || []).map(String));
      state.selectedIds = state.selectedIds.filter((id) => !drop.has(id));
    },
    setSelected: (state, action) => {
      state.selectedIds = Array.from(new Set((action.payload || []).map(String)));
    },
    clearSelection: (state) => {
      state.selectedIds = [];
    },
    clearBulkResult: (state) => {
      state.lastBulkResult = null;
    },
    pushAlert: (state, action) => {
      state.alerts.push(action.payload);
    },
    stashAlert: (state, action) => {
      state.storedAlerts.push(action.payload);
      try {
        localStorage.setItem(
          'campaign_stored_alerts',
          JSON.stringify(state.storedAlerts)
        );
      } catch (e) {
        console.error('[store] Failed to save stored alerts to localStorage:', e);
      }
    },
    clearStoredAlerts: (state) => {
      state.storedAlerts = [];
    },
    // Marks the alert closed so it can animate out; removeAlert drops it.
    closeAlert: (state, action) => {
      state.alerts = state.alerts.map((a) =>
        a.id === action.payload ? { ...a, open: false } : a
      );
    },
    removeAlert: (state, action) => {
      state.alerts = state.alerts.filter((a) => a.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCampaigns.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = mergeById(state.campaigns, action.payload.campaigns);
        if (action.payload.pagination) state.pagination = action.payload.pagination;
      })
      .addCase(loadCampaigns.rejected, (state) => {
        state.loading = false;
        // Only fall back to sample data when nothing has loaded at all.
        if (state.campaigns.length === 0) state.campaigns = sample;
      })
      .addCase(loadCampaignById.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadCampaignById.fulfilled, (state, action) => {
        state.loading = false;
        if (!action.payload) return;
        const { id, campaign } = action.payload;
        const map = new Map();
        for (const c of state.campaigns) {
          const cid = String(c.id || c._id || '');
          if (cid) map.set(cid, c);
        }
        map.set(id, { ...map.get(id), ...campaign, id, _id: id });
        state.campaigns = Array.from(map.values());
      })
      .addCase(loadCampaignById.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createCampaign.fulfilled, (state, action) => {
        state.campaigns = [action.payload, ...state.campaigns];
      })
      .addCase(updateCampaign.fulfilled, (state, action) => {
        const { id, campaign } = action.payload;
        state.campaigns = state.campaigns.map((c) =>
          String(c.id) === id || String(c._id) === id
            ? { ...c, ...campaign, id, _id: id }
            : c
        );
      })
      // All three audience actions answer with the rebuilt campaign, so they land the same way.
      .addCase(addAudience.fulfilled, (state, action) => {
        const { id, campaign } = action.payload;
        state.campaigns = state.campaigns.map((c) =>
          String(c.id) === id || String(c._id) === id ? { ...c, ...campaign, id, _id: id } : c
        );
      })
      .addCase(updateAudience.fulfilled, (state, action) => {
        const { id, campaign } = action.payload;
        state.campaigns = state.campaigns.map((c) =>
          String(c.id) === id || String(c._id) === id ? { ...c, ...campaign, id, _id: id } : c
        );
      })
      .addCase(deleteAudience.fulfilled, (state, action) => {
        const { id, campaign } = action.payload;
        state.campaigns = state.campaigns.map((c) =>
          String(c.id) === id || String(c._id) === id ? { ...c, ...campaign, id, _id: id } : c
        );
      })
      .addCase(removeCampaign.fulfilled, (state, action) => {
        state.campaigns = state.campaigns.filter(
          (c) => c.id !== action.payload && c._id !== action.payload
        );
      })
      .addCase(bulkCampaignAction.pending, (state) => {
        state.bulkBusy = true;
      })
      .addCase(bulkCampaignAction.rejected, (state) => {
        state.bulkBusy = false;
      })
      .addCase(bulkCampaignAction.fulfilled, (state, action) => {
        state.bulkBusy = false;
        if (!action.payload) return;
        const { action: bulkAction, results, summary, succeededIds } = action.payload;
        const succeeded = new Set(succeededIds);

        // Only worth a dialog when something did not go through.
        state.lastBulkResult = summary.failed > 0
          ? { action: bulkAction, results, summary }
          : null;

        if (bulkAction === 'delete') {
          state.campaigns = state.campaigns.filter(
            (c) => !succeeded.has(String(c.id)) && !succeeded.has(String(c._id))
          );
        } else if (bulkAction === 'edit') {
          // An edit can rewrite name, subject, body, sender, template and schedule at once.
          // Rather than reproduce all of that here, mark the rows stale: the broadcast the
          // server sends triggers a refetch, and that is the version worth trusting.
          const edited = new Set(succeededIds);
          state.campaigns = state.campaigns.map((c) =>
            edited.has(String(c.id)) || edited.has(String(c._id))
              ? { ...c, updatedAt: new Date().toISOString() }
              : c
          );
        } else {
          // The server reports the status it settled on ('sending' vs 'scheduled' on resume),
          // so take it from the result rather than assuming which one a resume produced.
          const statusById = new Map(
            results.filter((r) => r.success && r.status).map((r) => [String(r.id), capitalizeStatus(r.status)])
          );
          state.campaigns = state.campaigns.map((c) => {
            const next = statusById.get(String(c.id)) || statusById.get(String(c._id));
            return next ? { ...c, status: next, updatedAt: new Date().toISOString() } : c;
          });
        }

        // Rows that went through leave the selection; refused rows stay checked so the user
        // can see what still needs attention.
        state.selectedIds = state.selectedIds.filter((id) => !succeeded.has(id));
      })
      .addCase(toggleStatus.fulfilled, (state, action) => {
        if (!action.payload) return;
        const { id, campaign } = action.payload;
        state.campaigns = state.campaigns.map((c) => (c.id === id ? campaign : c));
      });
  },
});

export const {
  add,
  update,
  prependCampaign,
  dropCampaign,
  duplicate,
  setCampaignStatus,
  toggleSelected,
  selectMany,
  deselectMany,
  setSelected,
  clearSelection,
  clearBulkResult,
  pause,
  resume,
  declareWinner,
  pushAlert,
  stashAlert,
  clearStoredAlerts,
  closeAlert,
  removeAlert,
} = emailCampaignsSlice.actions;

// Closes the alert, then drops it once the 500ms fade-out has run.
export const dismissAlert = (id) => (dispatch) => {
  dispatch(closeAlert(id));
  setTimeout(() => dispatch(removeAlert(id)), 500);
};

// `addAlert` was the store's action name; keep it as the public entry point.
export const addAlert = showAlert;

// Store getters become selectors / plain helpers.
export const selectCampaignMetrics = (state, id) => {
  const campaign = state.emailCampaigns.campaigns.find((c) => c.id === id);
  if (!campaign) return null;
  if (campaign.metrics) return campaign.metrics;

  const m = simulateMetrics({ seed: id, sent: 1000 });
  return {
    ...m,
    timeseries: m.timeseries.map((t, i) => ({ t: i, opens: t.opens, clicks: t.clicks })),
  };
};

export const getEstimatedReach = (campaign, contacts) =>
  calculateEstimatedReach(campaign, contacts);

export const selectSelectedIds = (state) => state.emailCampaigns.selectedIds;

export const selectCampaignsByStatus = (state, status) =>
  state.emailCampaigns.campaigns.filter((c) => c.status === status);

export const selectRecentCampaigns = (state, limit = 5) =>
  [...state.emailCampaigns.campaigns]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, limit);

export const searchCampaigns = (campaigns, query) => {
  if (!query) return campaigns;
  const lowerQuery = query.toLowerCase();
  return campaigns.filter(
    (campaign) =>
      campaign.name.toLowerCase().includes(lowerQuery) ||
      campaign.subject.toLowerCase().includes(lowerQuery) ||
      campaign.status.toLowerCase().includes(lowerQuery)
  );
};

export default emailCampaignsSlice.reducer;
